import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FiDownload, FiCheck, FiGift, FiUsers, FiMail, FiPhone, FiCalendar, FiExternalLink } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const ConfirmationPage = ({ customerData, hasBump }) => {
  const [emailSent, setEmailSent] = useState(false);
  const [downloadStarted, setDownloadStarted] = useState(false);

  useEffect(() => {
    // Track conversion in analytics
    if (window.gtag) {
      window.gtag('event', 'purchase', {
        currency: 'INR',
        value: hasBump ? 39600 : 9700, // ₹396 or ₹97 in paise
        items: [
          {
            item_id: 'diwali-ebook',
            item_name: '24 Ways to Double Your Sales',
            price: 9700,
            quantity: 1
          },
          ...(hasBump ? [{
            item_id: 'expert-call',
            item_name: '1-to-1 Expert Call',
            price: 29900,
            quantity: 1
          }] : [])
        ]
      });
    }
  }, [hasBump]);

  const handleDownload = () => {
    setDownloadStarted(true);
    // Open eBook download link
    window.open('https://abdul.cc/100ways2x', '_blank');
    setTimeout(() => setDownloadStarted(false), 3000);
  };

  const handleSendEmail = () => {
    setEmailSent(true);
    // Simulate sending email with access details
    setTimeout(() => setEmailSent(false), 3000);
  };

  const bonuses = [
    { name: '24 Ways to Double Your Sales', value: '₹997', icon: '📚' },
    { name: 'Diwali Workshop Access', value: '₹4,999', icon: '🎯' },
    { name: 'WhatsApp & Email Automation Templates', value: '₹3,999', icon: '⚡' },
    { name: 'Free CRM & Automation Software', value: '₹5,999', icon: '🛠️' },
    { name: 'Bulk WhatsApp Tutorial', value: '₹6,999', icon: '📱' },
    { name: 'Lead Generation Strategy Playbook', value: '₹3,499', icon: '🎯' }
  ];

  if (hasBump) {
    bonuses.push({ name: '1-to-1 Expert Strategy Call (30 mins)', value: '₹2,999', icon: '👥' });
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-orange-50">
      <div className="sparkles-bg"></div>
      
      <motion.div
        className="container mx-auto px-4 py-12"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        {/* Success Header */}
        <div className="text-center mb-12">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <SafeIcon icon={FiCheck} className="w-10 h-10 text-green-600" />
          </div>
          
          <h1 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
            Congratulations! 🎉
          </h1>
          <p className="text-xl text-gray-600 mb-2">
            Your purchase was successful!
          </p>
          <p className="text-lg text-gray-500">
            Thank you, <span className="font-semibold">{customerData?.name || 'Valued Customer'}</span>!
          </p>
        </div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
          
          {/* Left: Download & Access */}
          <div className="lg:col-span-2 space-y-6">
            
            {/* eBook Download */}
            <motion.div
              className="bg-white p-8 rounded-3xl shadow-lg"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
            >
              <div className="flex items-center mb-6">
                <div className="text-4xl mr-4">📚</div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-800">Your eBook is Ready!</h2>
                  <p className="text-gray-600">Instant download available now</p>
                </div>
              </div>
              
              <a
                href="https://abdul.cc/100ways2x"
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full glow-button text-white px-8 py-4 rounded-xl font-bold text-lg mb-4 text-center hover:opacity-90 transition-opacity"
              >
                <SafeIcon icon={FiDownload} className="w-5 h-5 inline mr-2" />
                <span>{downloadStarted ? 'Downloading...' : 'Download eBook Now'}</span>
              </a>
              
              <button
                onClick={handleSendEmail}
                className="w-full bg-gray-100 text-gray-700 px-6 py-3 rounded-xl font-semibold hover:bg-gray-200 transition-colors flex items-center justify-center space-x-2"
              >
                <SafeIcon icon={FiMail} className="w-5 h-5" />
                <span>{emailSent ? '✓ Sent to Email' : 'Send Backup to Email'}</span>
              </button>
              
              {emailSent && (
                <p className="text-center text-green-600 text-sm mt-2">
                  ✓ Access details sent to {customerData?.email}
                </p>
              )}
            </motion.div>

            {/* WhatsApp Community */}
            <motion.div
              className="bg-gradient-to-r from-green-500 to-green-600 text-white p-8 rounded-3xl shadow-lg"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
            >
              <div className="flex items-center mb-6">
                <div className="text-4xl mr-4">👥</div>
                <div>
                  <h2 className="text-2xl font-bold mb-2">Join Our Exclusive Community!</h2>
                  <p className="text-green-100">Connect with 1000+ business owners like you</p>
                </div>
              </div>
              
              <div className="space-y-3 mb-6">
                <div className="flex items-center space-x-3">
                  <SafeIcon icon={FiUsers} className="w-5 h-5" />
                  <span>Daily tips & strategies</span>
                </div>
                <div className="flex items-center space-x-3">
                  <SafeIcon icon={FiGift} className="w-5 h-5" />
                  <span>Exclusive resources & templates</span>
                </div>
                <div className="flex items-center space-x-3">
                  <SafeIcon icon={FiPhone} className="w-5 h-5" />
                  <span>Direct support from experts</span>
                </div>
                <div className="flex items-center space-x-3">
                  <SafeIcon icon={FiCalendar} className="w-5 h-5" />
                  <span>Weekly live Q&A sessions</span>
                </div>
              </div>
              
              <a
                href="https://abdul.cc/100ways2xcommunity"
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full bg-white text-green-600 px-8 py-4 rounded-xl font-bold text-lg hover:bg-gray-100 transition-colors flex items-center justify-center space-x-2"
              >
                <span>📱</span>
                <span>Join WhatsApp Community</span>
                <SafeIcon icon={FiExternalLink} className="w-5 h-5" />
              </a>
            </motion.div>

            {/* Expert Call (if purchased) */}
            {hasBump && (
              <motion.div
                className="bg-gradient-to-r from-orange-500 to-red-500 text-white p-8 rounded-3xl shadow-lg"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 }}
              >
                <div className="flex items-center mb-6">
                  <div className="text-4xl mr-4">🎯</div>
                  <div>
                    <h2 className="text-2xl font-bold mb-2">Expert Strategy Call Booked!</h2>
                    <p className="text-orange-100">You'll receive scheduling details within 24 hours</p>
                  </div>
                </div>
                
                <div className="bg-white/20 p-4 rounded-xl">
                  <p className="font-semibold mb-2">What to prepare:</p>
                  <ul className="space-y-1 text-sm">
                    <li>• Your current business challenges</li>
                    <li>• Goals for the next 90 days</li>
                    <li>• Any specific questions about automation</li>
                  </ul>
                </div>
              </motion.div>
            )}
          </div>

          {/* Right: Bonuses List */}
          <div className="space-y-6">
            <motion.div
              className="bg-white p-8 rounded-3xl shadow-lg"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.5 }}
            >
              <h3 className="text-2xl font-bold text-gray-800 mb-6">Everything You Get</h3>
              
              <div className="space-y-4">
                {bonuses.map((bonus, index) => (
                  <div key={index} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-xl">
                    <div className="text-2xl">{bonus.icon}</div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-800">{bonus.name}</h4>
                      <p className="text-sm text-orange-600 font-medium">{bonus.value} Value</p>
                    </div>
                    <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-500 flex-shrink-0 mt-1" />
                  </div>
                ))}
              </div>
              
              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-gray-600">Total Value:</span>
                  <span className="text-xl font-bold text-gray-800">
                    ₹{hasBump ? '29,491' : '26,492'}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">You Paid:</span>
                  <span className="text-xl font-bold text-green-600">
                    ₹{hasBump ? '396' : '97'}
                  </span>
                </div>
                <div className="text-center mt-4 text-sm text-gray-500">
                  You saved {hasBump ? '98.7%' : '99.6%'}!
                </div>
              </div>
            </motion.div>

            {/* Support */}
            <motion.div
              className="bg-purple-50 p-6 rounded-2xl border-2 border-purple-200"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.6 }}
            >
              <h4 className="font-bold text-gray-800 mb-3">Need Help?</h4>
              <div className="space-y-2 text-sm">
                <p>📧 support@yinguru.com</p>
                <p>📞 +91-9884899080</p>
                <p>⏰ 9 AM - 9 PM IST</p>
              </div>
              <p className="text-xs text-gray-600 mt-3">
                We're here to help you succeed!
              </p>
            </motion.div>
          </div>
        </div>

        {/* Bottom CTA */}
        <motion.div
          className="text-center mt-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
        >
          <p className="text-lg text-gray-700 mb-4">
            Ready to transform your business this Diwali? 🚀
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="https://abdul.cc/100ways2x"
              target="_blank"
              rel="noopener noreferrer"
              className="glow-button text-white px-8 py-4 rounded-xl font-bold flex items-center justify-center space-x-2 hover:opacity-90 transition-opacity"
            >
              <SafeIcon icon={FiDownload} className="w-5 h-5" />
              <span>Start Reading Now</span>
            </a>
            <a
              href="https://abdul.cc/100ways2xcommunity"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-green-500 text-white px-8 py-4 rounded-xl font-bold hover:bg-green-600 transition-colors flex items-center justify-center space-x-2"
            >
              <span>📱</span>
              <span>Join WhatsApp Community</span>
            </a>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default ConfirmationPage;